"use client";

import React, { useState, useRef } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import type {
  IPricingAsset,
  IPricingAccountType,
  IPricingDetail,
  IPricingType,
  IFieldDescriptions,
} from "@/sanity/pages/PageHomeTemplate";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { CTAButton } from "@/shadcn/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";
import checkbg from "@/public/pricing-table/checkbg.jpg";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/shadcn/ui/popover";

// PricingCard Component
interface PricingCardProps {
  detail: IPricingDetail;
  detailIndex: number;
  getActiveTab: (index: number) => "challenge" | "funded";
  setActiveTab: (index: number, tab: "challenge" | "funded") => void;
  fieldDescriptions?: IFieldDescriptions;
  InfoRow: React.FC<{
    label: string;
    value: string | number;
    showBorder?: boolean;
    description?: string;
  }>;
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
    >
      <path
        d="M10 7H10.01M9 10H10V14H11M10 1C17.2 1 19 2.8 19 10C19 17.2 17.2 19 10 19C2.8 19 1 17.2 1 10C1 2.8 2.8 1 10 1Z"
        stroke="#BABABA"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const tooltip = (title: React.ReactNode, content: React.ReactNode) => (
  <div className="hidden lg:block">
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>{title}</TooltipTrigger>
        <TooltipContent className="bg-transparent" side="right">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
);

const popover = (title: React.ReactNode, content: React.ReactNode) => (
  <div className="mt-2 lg:hidden">
    <Popover>
      <PopoverTrigger>{title}</PopoverTrigger>
      <PopoverContent className="w-auto !bg-transparent !border-none shadow-none [&>div]:!bg-transparent">
        {content}
      </PopoverContent>
    </Popover>
  </div>
);

function PricingCard({
  detail,
  detailIndex,
  getActiveTab,
  setActiveTab,
  fieldDescriptions,
  InfoRow,
}: PricingCardProps) {
  const challengeType = detail.types?.find((t) => t.type === "challenge");
  const fundedType = detail.types?.find((t) => t.type === "funded");

  // Determine default tab if not set
  const currentTab = getActiveTab(detailIndex);
  let effectiveTab = currentTab;

  // If current tab is not available, switch to available one
  if (currentTab === "challenge" && !challengeType && fundedType) {
    effectiveTab = "funded";
  } else if (currentTab === "funded" && !fundedType && challengeType) {
    effectiveTab = "challenge";
  } else if (!challengeType && fundedType) {
    effectiveTab = "funded";
  } else if (!fundedType && challengeType) {
    effectiveTab = "challenge";
  }

  const activeType = effectiveTab === "challenge" ? challengeType : fundedType;

  return (
    <div className="flex flex-col gap-[.75rem]">
      {/* Header */}
      <div className="flex flex-col gap-[1.5rem] bg-[#F3F7F5] rounded-[1.5rem] p-[3rem]">
        <h3 className="text-[2.75rem] lg:text-[3.25rem] font-[500] font-geist text-[#103037] mb-0">
          {detail.name}
        </h3>

        {/* Tabs - Only show if both types exist */}
        {challengeType && fundedType && (
          <div className="flex bg-white rounded-[1.25rem] p-[0.5rem]">
            {challengeType && (
              <button
                onClick={() => setActiveTab(detailIndex, "challenge")}
                className={cn(
                  "flex-1 px-[2rem] py-[1rem] rounded-[.88rem] text-[#102F37] text-[1.75rem] font-[500] transition-all",
                  effectiveTab === "challenge" ? "bg-[#DAF2E0]" : "",
                )}
              >
                Challenge
              </button>
            )}
            {fundedType && (
              <button
                onClick={() => setActiveTab(detailIndex, "funded")}
                className={cn(
                  "flex-1 px-[2rem] py-[1rem] rounded-[.5rem] text-[#102F37] text-[1.75rem] font-[400] transition-all",
                  effectiveTab === "funded" ? "bg-[#DAF2E0]" : "",
                )}
              >
                Funded
              </button>
            )}
          </div>
        )}
      </div>

      {/* Details */}
      {activeType && (
        <div className="flex flex-col rounded-[1.25rem] overflow-hidden">
          <h4 className="text-[2.25rem] font-[500] text-black mb-0 bg-[#DAF2E0] py-[2.25rem] px-[3rem]">
            {effectiveTab === "challenge" ? "Challenge Rules" : "Funded Rules"}
          </h4>
          <div className="flex flex-col gap-[1.88rem] bg-[#E6EDE9]/40 pt-[2rem] pb-[2.25rem] px-[3rem]">
            {/* Profit Target (Challenge only) */}
            {activeType.profitTarget && (
              <InfoRow
                label="Profit Target"
                value={activeType.profitTarget}
                description={fieldDescriptions?.profitTargetDescription}
              />
            )}

            {/* Maximum Drawdown (Challenge only) */}
            {activeType.maximumDrawdown && (
              <InfoRow
                label="Maximum Drawdown"
                value={activeType.maximumDrawdown}
                description={fieldDescriptions?.maximumDrawdownDescription}
              />
            )}

            {/* Trailing Max Drawdown (Funded only) */}
            {activeType.trailingMaxDrawdown && (
              <InfoRow
                label="Trailing Max Drawdown"
                value={activeType.trailingMaxDrawdown}
                description={fieldDescriptions?.trailingMaxDrawdownDescription}
              />
            )}

            {/* Drawdown Mode */}
            {activeType.drawdownMode && (
              <InfoRow
                label="Drawdown Mode"
                value={activeType.drawdownMode}
                description={fieldDescriptions?.drawdownModeDescription}
              />
            )}

            {/* Position Loss (flat) */}
            {activeType.positionLossFlat && (
              <InfoRow
                label="Position Loss (flat)"
                value={activeType.positionLossFlat}
                description={fieldDescriptions?.positionLossFlatDescription}
              />
            )}

            {/* Consistency */}
            {activeType.consistency && (
              <InfoRow
                label="Consistency"
                value={activeType.consistency}
                description={fieldDescriptions?.consistencyDescription}
              />
            )}

            {/* Contracts */}
            {activeType.contracts && (
              <InfoRow
                label="Contracts"
                value={activeType.contracts}
                description={fieldDescriptions?.contractsDescription}
              />
            )}

            {/* Challenge Duration (Challenge only) */}
            {activeType.challengeDuration && (
              <InfoRow
                label="Challenge Duration"
                value={activeType.challengeDuration}
              />
            )}

            {/* Activation Fee (Challenge only) */}
            {activeType.activationFee && (
              <InfoRow
                label="Activation Fee"
                value={activeType.activationFee}
                showBorder={false}
              />
            )}

            {/* Minimum Profits (Funded only) */}
            {/*{activeType.minimumProfits && (
              <InfoRow
                label="Minimum Payout"
                value={activeType.minimumProfits}
                description={fieldDescriptions?.minimumProfitsDescription}
              />
            )}*/}

            {/* Days to Payout (Funded only) */}
            {activeType.daysToPayout && (
              <InfoRow
                label="Days to Payout"
                value={activeType.daysToPayout}
                description={fieldDescriptions?.daysToPayoutDescription}
              />
            )}

            {/* Profit Split (Funded only) */}
            {activeType.profitSplit && (
              <InfoRow
                label="Profit Split"
                value={activeType.profitSplit}
                showBorder={false}
                description={fieldDescriptions?.profitSplitDescription}
              />
            )}

            <div className="flex flex-col lg:gap-[1.5rem] gap-[1rem] pt-[1rem] lg:pt-[1.75rem]">
              {/* Price Display */}
              {detail.price && (
                <div className="flex items-baseline gap-[.75rem]">
                  <span className="text-[3.5rem] lg:text-[4rem] font-[500] font-geist text-[#103037]">
                    {detail.hasDiscount && detail.discountedPrice
                      ? detail.discountedPrice
                      : detail.price}
                  </span>
                  {detail.hasDiscount && detail.discountedPrice && (
                    <span className="text-[2.25rem] font-[500] font-geist text-[#103037]/50 line-through">
                      {detail.price}
                    </span>
                  )}
                  {detail.feeText && (
                    <span className="text-[1.75rem] font-[500] font-geist text-[#103037]/60">
                      {detail.feeText}
                    </span>
                  )}
                </div>
              )}

              {/* CTA Button */}
              {detail.cta && <CTAButton data={detail.cta} className="w-full" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PricingTableNew({
  pricingAssets = [],
  fieldDescriptions,
  lang,
  stepOneTitle = "Asset",
  stepOneDescription,
  stepTwoTitle = "Account Type",
  stepTwoDescription,
}: {
  pricingAssets?: IPricingAsset[];
  fieldDescriptions?: IFieldDescriptions;
  lang: "en" | "de" | "br";
  stepOneTitle?: string;
  stepOneDescription?: string;
  stepTwoTitle?: string;
  stepTwoDescription?: string;
}) {
  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number>(0);
  const [selectedAccountTypeIndex, setSelectedAccountTypeIndex] =
    useState<number>(0);
  const [activeTabs, setActiveTabs] = useState<
    Record<number, "challenge" | "funded">
  >({});
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  const selectedAsset = pricingAssets[selectedAssetIndex];
  const selectedAccountType =
    selectedAsset?.accountType?.[selectedAccountTypeIndex];

  React.useEffect(() => {
    if (!carouselApi) return;

    const updateCarousel = () => {
      const prev = carouselApi.canScrollPrev();
      const next = carouselApi.canScrollNext();

      setCanScrollPrev(prev);
      setCanScrollNext(next);
      setCanScroll(prev || next);

      // Calculate scroll progress based on current slide (snap-based)
      const currentIndex = carouselApi.selectedScrollSnap();
      const totalSlides = carouselApi.scrollSnapList().length;

      // Calculate percentage: current / total (accounting for 0-based index)
      const progress =
        totalSlides > 1 ? ((currentIndex + 1) / totalSlides) * 100 : 0;
      setScrollProgress(progress);
    };

    updateCarousel();
    carouselApi.on("select", updateCarousel);
    carouselApi.on("reInit", updateCarousel);

    return () => {
      carouselApi.off("select", updateCarousel);
      carouselApi.off("reInit", updateCarousel);
    };
  }, [carouselApi]);

  const getActiveTab = (index: number) => {
    return activeTabs[index] || "challenge";
  };

  const setActiveTab = (index: number, tab: "challenge" | "funded") => {
    setActiveTabs((prev) => ({ ...prev, [index]: tab }));
  };

  const InfoRow = ({
    label,
    value,
    showBorder = true,
    description,
  }: {
    label: string;
    value: string | number;
    showBorder?: boolean;
    description?: string;
  }) => (
    <div
      className={cn(
        "flex justify-between items-center pb-[1.88rem]",
        showBorder && "border-b border-#CECECE/70 border-dashed",
      )}
    >
      <div className="flex items-center gap-[1.75rem]">
        {description && (
          <>
            {tooltip(
              <div className="relative cursor-pointer group h-[2.25rem] w-[2.25rem]">
                <InfoIcon className="!w-full" />
              </div>,
              <div className="text-black shadow-lg p-[2rem] bg-white z-[9999] rounded-[1.5rem]">
                <p className="m-0 text-[2rem] w-[35.5rem]">{description}</p>
              </div>,
            )}
            {popover(
              <div className="relative cursor-pointer group h-[2.25rem] w-[2.25rem]">
                <InfoIcon className="!w-full" />
              </div>,
              <div className="text-black shadow-lg p-[2rem] bg-white z-[9999] rounded-[1.5rem]">
                <p className="m-0 text-[2rem] w-[35.5rem]">{description}</p>
              </div>,
            )}
          </>
        )}
        <span className="text-[1.75rem] font-geist text-[#103037]">
          {label}
        </span>
      </div>
      <span className="text-[1.75rem] font-geist text-[#103037]/50">
        {value}
      </span>
    </div>
  );

  // Get all detail items for the selected account type
  const detailItems = selectedAccountType?.details || [];

  // Debug: Show message if no data
  if (!pricingAssets || pricingAssets.length === 0) {
    return (
      <div className="flex flex-col w-full gap-[2rem]">
        <div className="flex relative flex-col bg-white/90 p-[2.5rem] gap-[3rem] lg:gap-[6rem] lg:py-[4rem] lg:px-[6rem] rounded-[2rem] lg:rounded-[3rem]">
          <div className="text-center py-[4rem]">
            <h3 className="text-[2.5rem] text-[#103037] mb-[1rem]">
              No Pricing Data Available
            </h3>
            <p className="text-[1.75rem] text-[#103037]/60">
              Please add pricing assets in Sanity CMS.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full gap-[2rem]">
      {/* Asset and Account Type Selection */}
      <div className="flex relative flex-col bg-white/90 p-[2.5rem] gap-[3rem] lg:gap-[6rem] lg:py-[4rem] lg:px-[6rem] rounded-[2rem] lg:rounded-[3rem]">
        {/* Asset Selection */}
        <div className="flex flex-col gap-[1.5rem]">
          <h4 className="flex items-center gap-[1rem] text-[3rem] lg:text-[3.25rem] font-geist font-[500] text-[#103037] mb-0">
            1. {stepOneTitle}
            {stepOneDescription && (
              <>
                {tooltip(
                  <div className="relative cursor-pointer group  h-[2.25rem] w-[2.25rem]">
                    <InfoIcon className="!w-full" />
                  </div>,
                  <div className="text-black shadow-lg p-[2rem] bg-white z-[9999] rounded-[1.5rem]">
                    <p className="m-0 text-[2rem] w-[35.5rem]">
                      {stepOneDescription}
                    </p>
                  </div>,
                )}
                {popover(
                  <div className="relative cursor-pointer group  h-[2.25rem] w-[2.25rem]">
                    <InfoIcon className="!w-full" />
                  </div>,
                  <div className="text-black shadow-lg p-[2rem] bg-white z-[9999] rounded-[1.5rem]">
                    <p className="m-0 text-[2rem] w-[35.5rem]">
                      {stepOneDescription}
                    </p>
                  </div>,
                )}
              </>
            )}
          </h4>
          <div className="flex flex-col lg:flex-row gap-[1.5rem] flex-wrap">
            {pricingAssets.map((asset, assetIndex) => (
              <div key={assetIndex} className="relative">
                {selectedAssetIndex === assetIndex && (
                  <Image
                    src={checkbg}
                    alt=""
                    className="absolute inset-0 z-0 !w-full !h-full object-cover pointer-events-none rounded-[1.375rem]"
                  />
                )}
                <button
                  key={assetIndex}
                  onClick={() => {
                    setSelectedAssetIndex(assetIndex);
                    setSelectedAccountTypeIndex(0);
                  }}
                  className={cn(
                    "relative flex items-center gap-[1.75rem] p-[2rem] lg:py-[2.5rem] lg:px-[3rem] rounded-[1.375rem] text-[2rem] lg:text-[2.25rem] font-[400] transition-all w-full lg:w-fit",
                    selectedAssetIndex === assetIndex
                      ? "bg-gradient-to-tr from-[#103037] from-[55%] to-[#103037]/70 text-white"
                      : "bg-[#F3F7F5] text-[#102F37] hover:bg-[#E6EDE9]",
                  )}
                >
                  {selectedAssetIndex === assetIndex ? (
                    <div className="relative z-[1] flex items-center justify-center w-[3.25rem] h-[3.25rem] lg:w-[3.5rem] lg:h-[3.5rem] rounded-full bg-gradient-to-bl from-[#277283] to-[#277283]/0 flex-shrink-0">
                      <Check className="w-[1.65rem] h-[1.65rem] lg:w-[1.8rem] lg:h-[1.8rem] ml-[0.15rem]" />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-[3rem] h-[3rem] lg:w-[3.35rem] lg:h-[3.35rem] rounded-full border border-[#AFBBBC]" />
                  )}
                  {asset.name}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Account Type Selection */}
        <div className="flex flex-col gap-[1.5rem]">
          <h4 className="flex items-center gap-[1rem] text-[3rem] lg:text-[3.25rem] font-geist font-[500] text-[#103037] mb-0">
            2. {stepTwoTitle}
            {stepTwoDescription && (
              <>
                {tooltip(
                  <div className="relative cursor-pointer group  h-[2.25rem] w-[2.25rem]">
                    <InfoIcon className="!w-full" />
                  </div>,
                  <div className="text-black shadow-lg p-[2rem] bg-white z-[9999] rounded-[1.5rem]">
                    <p className="m-0 text-[2rem] w-[35.5rem]">
                      {stepTwoDescription}
                    </p>
                  </div>,
                )}
                {popover(
                  <div className="relative cursor-pointer group  h-[2.25rem] w-[2.25rem]">
                    <InfoIcon className="!w-full" />
                  </div>,
                  <div className="text-black shadow-lg p-[2rem] bg-white z-[9999] rounded-[1.5rem]">
                    <p className="m-0 text-[2rem] w-[35.5rem]">
                      {stepTwoDescription}
                    </p>
                  </div>,
                )}
              </>
            )}
          </h4>
          {!selectedAsset?.accountType ||
          selectedAsset.accountType.length === 0 ? (
            <div className="text-center py-[2rem] text-[1.75rem] text-[#103037]/60">
              No account types available for this asset.
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-[1.5rem] flex-wrap">
              {selectedAsset?.accountType?.map((accountType, index) => (
                <div key={index} className="relative">
                  {selectedAccountTypeIndex === index && (
                    <Image
                      src={checkbg}
                      alt=""
                      className="absolute inset-0 z-0 !w-full !h-full object-cover pointer-events-none rounded-[1.375rem]"
                    />
                  )}
                  <button
                    key={index}
                    onClick={() => setSelectedAccountTypeIndex(index)}
                    className={cn(
                      "relative flex flex-col text-start p-[2rem] lg:pl-[3rem] lg:pr-[5.5rem] lg:py-[2.5rem] rounded-[1.375rem] transition-all w-full lg:w-fit",
                      selectedAccountTypeIndex === index
                        ? "bg-gradient-to-tr from-[#103037] from-[55%] to-[#103037]/70 text-white"
                        : "bg-[#F3F7F5] text-[#102F37] hover:bg-[#E6EDE9]",
                    )}
                  >
                    <div className="flex items-center gap-[1.75rem]">
                      {selectedAccountTypeIndex === index ? (
                        <div className="flex items-center justify-center w-[3.25rem] h-[3.25rem] lg:w-[3.5rem] lg:h-[3.5rem] rounded-full bg-gradient-to-bl from-[#277283] to-[#277283]/0 flex-shrink-0">
                          <Check className="w-[1.65rem] h-[1.65rem] lg:w-[1.8rem] lg:h-[1.8rem] ml-[0.15rem]" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center w-[3rem] h-[3rem] lg:w-[3.35rem] lg:h-[3.35rem] rounded-full border border-[#AFBBBC]" />
                      )}
                      <div className="relative">
                        <span
                          className={cn(
                            "font-[500] text-[2rem] lg:text-[2.25rem] transition-all",
                            selectedAccountTypeIndex === index
                              ? "text-white"
                              : "text-[#102F37]",
                          )}
                        >
                          {accountType.title}
                        </span>
                        {accountType.description && (
                          <p
                            className={cn(
                              "text-[1.5rem] opacity-50 mb-0",
                              selectedAccountTypeIndex === index
                                ? "text-white"
                                : "text-[#102F37]",
                            )}
                          >
                            {accountType.description}
                          </p>
                        )}

                        {accountType.highlight && (
                          <span className="absolute bottom-[-3.8rem] left-0 bg-[#75C188] text-white text-[1.3rem] font-[600] px-[1.5rem] py-[0.5rem] rounded-[0.75rem] z-50">
                            {accountType.highlight}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pricing Cards Grid */}
      <div className="relative p-[3rem] lg:p-[6rem] bg-white/90 rounded-[2rem] lg:rounded-[3rem] overflow-clip">
        {!detailItems || detailItems.length === 0 ? (
          <div className="text-center py-[6rem] text-[1.75rem] text-[#103037]/60">
            No pricing details available. Please select an account type or add
            details in Sanity CMS.
          </div>
        ) : (
          <Carousel
            setApi={setCarouselApi}
            opts={{
              align: "start",
              loop: false,
              // containScroll: "trimSnaps",
            }}
            className="w-full [&>div]:overflow-visible"
          >
            <CarouselContent className="gap-[1.88rem] px-0">
              {detailItems.map((detail, detailIndex) => (
                <CarouselItem
                  key={detailIndex}
                  className="basis-[min(37rem,100%)] lg:basis-[55.5rem] pl-0"
                >
                  <PricingCard
                    detail={detail}
                    detailIndex={detailIndex}
                    getActiveTab={getActiveTab}
                    setActiveTab={setActiveTab}
                    fieldDescriptions={fieldDescriptions}
                    InfoRow={InfoRow}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>

            {/* Navigation Arrows */}
            {canScroll && (
              <div className="flex items-center justify-center gap-[2rem] mt-[4.5rem]">
                <button
                  onClick={() => carouselApi?.scrollPrev()}
                  disabled={!canScrollPrev}
                  className="flex items-center justify-center w-[4.5rem] h-[4.5rem] rounded-full bg-[#103037] text-[#A3DEB2] hover:bg-[#103037]/90 transition-all duration-300 ease-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-[2.5rem] h-[2.5rem]" />
                </button>

                <div className="h-[0.5rem] w-[19.6805rem] bg-[#E5E5E5] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#A3DEB2] rounded-full transition-all"
                    style={{ width: `${scrollProgress}%` }}
                  ></div>
                </div>

                <button
                  onClick={() => carouselApi?.scrollNext()}
                  disabled={!canScrollNext}
                  className="flex items-center justify-center w-[4.5rem] h-[4.5rem] rounded-full bg-[#103037] text-[#A3DEB2] hover:bg-[#103037]/90 transition-all duration-300 ease-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-[2.5rem] h-[2.5rem]" />
                </button>
              </div>
            )}
          </Carousel>
        )}
      </div>
    </div>
  );
}
